import { apiRequest } from "./client";
import type { EntityDto, TreeRelationDto } from "./types";

export type CustomPropertyPayload = {
	key: string;
	value?: string;
};

export type CreateEntityPayload = {
	type: string;
	title: string;
	imageUrl?: string;
	imageName?: string;
	// Person
	gender?: string;
	genderOther?: string;
	alias?: string;
	born?: string;
	personTitle?: string;
	nickname?: string;
	description?: string;
	specialty?: string;
	// Place
	latitude?: number;
	longitude?: number;
	country?: string;
	world?: string;
	placeType?: string;
	region?: string;
	// Object
	objectType?: string;
	origin?: string;
	material?: string;
	currentLocation?: string;
	era?: string;
	ownerEntityId?: string;
	// Creature (also reuses origin)
	creatureType?: string;
	speciesName?: string;
	alignment?: string;
	traits?: string;
	// Organization (also reuses region)
	organizationType?: string;
	motto?: string;
	founded?: string;
	// Event (also reuses era)
	eventType?: string;
	occurredOn?: string;
	participants?: string;
	outcome?: string;
	// Species (also reuses world and traits)
	classification?: string;
	habitat?: string;
	lifespan?: string;
	language?: string;
	// Concept
	conceptType?: string;
	domain?: string;
	source?: string;
	symbols?: string;
	scope?: string;
	status?: string;
	// Custom (also reuses world)
	customTypeLabel?: string;
	parentGroup?: string;
	tags?: string;
	customProperties?: CustomPropertyPayload[];
	/** External reference URLs attached to the node. */
	referenceLinks?: string[];
	/** Pre-uploaded gallery images. The one marked default (or the first) becomes the node's default image. */
	images?: EntityImageInputPayload[];
};

/** A gallery image supplied at create time (already uploaded to blob storage). */
export type EntityImageInputPayload = {
	url: string;
	name?: string;
	isDefault: boolean;
};

export type UpdateEntityPayload = Omit<CreateEntityPayload, "type">;

export const createEntity = (treeId: string, payload: CreateEntityPayload, token: string): Promise<EntityDto> =>
	apiRequest<EntityDto>(`/trees/${treeId}/entities`, { method: "POST", body: payload, token });

export const updateEntity = (entityId: string, payload: UpdateEntityPayload, token: string): Promise<EntityDto> =>
	apiRequest<EntityDto>(`/entities/${entityId}`, { method: "PUT", body: payload, token });

export const deleteEntity = (entityId: string, token: string): Promise<void> =>
	apiRequest<void>(`/entities/${entityId}`, { method: "DELETE", token });

export const getTreeEntities = (treeId: string, token: string): Promise<EntityDto[]> =>
	apiRequest<EntityDto[]>(`/trees/${treeId}/entities`, { token });

export type EntityPositionPayload = { entityId: string; x: number; y: number };

export type TreeLinkPositionPayload = { linkId: string; x: number; y: number };

/** Saves canvas positions (entity nodes and linked-tree nodes) so a hand-arranged layout survives reloads. */
export const saveEntityPositions = (
	treeId: string,
	positions: EntityPositionPayload[],
	linkPositions: TreeLinkPositionPayload[],
	token: string
): Promise<void> =>
	apiRequest<void>(`/trees/${treeId}/entities/positions`, {
		method: "PUT",
		body: { positions, linkPositions },
		token
	});

/** Clears saved canvas positions so the auto-layout arranges the tree fresh. */
export const resetEntityPositions = (treeId: string, token: string): Promise<void> =>
	apiRequest<void>(`/trees/${treeId}/entities/positions`, { method: "DELETE", token });

export const getTreeRelations = (treeId: string, token: string): Promise<TreeRelationDto[]> =>
	apiRequest<TreeRelationDto[]>(`/trees/${treeId}/relations`, { token });

/** Links an entity node to another tree (shown as a tree node on the canvas). */
export const addEntityTreeLink = (entityId: string, linkedTreeId: string, token: string): Promise<void> =>
	apiRequest<void>(`/entities/${entityId}/tree-links`, { method: "POST", body: { linkedTreeId }, token });

export const removeEntityTreeLink = (linkId: string, token: string): Promise<void> =>
	apiRequest<void>(`/tree-links/${linkId}`, { method: "DELETE", token });
